import { ReactComponent as TrashIcon } from "../../assets/icons/tables/trash-icon.svg";
import { useState } from "react";
import { DeleteModal } from "../../components";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  ISentNotification,
  useNotifications,
} from "../../store/useNotifications";

dayjs.extend(relativeTime);

// The backend only queues a notification; a worker sends it in batches and
// reports progress. Rows created before that existed have no status and
// fall back to the old date-based label.
const describeStatus = (
  item: ISentNotification
): { label: string; className: string; percent?: number } => {
  const scheduledInFuture = dayjs(item?.scheduleDate).isAfter(dayjs());
  switch (item?.status) {
    case "PENDING":
      return scheduledInFuture
        ? { label: "Scheduled", className: "bg-orange-100 text-orange-700" }
        : { label: "Queued", className: "bg-gray-100 text-gray-700" };
    case "RUNNING": {
      const total = item?.progress?.usersTotal ?? 0;
      const done = item?.progress?.usersDone ?? 0;
      const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
      return {
        label: `Sending ${percent}%`,
        className: "bg-blue-100 text-blue-700",
        percent,
      };
    }
    case "DONE":
      return { label: "Sent", className: "bg-green-100 text-green-700" };
    case "FAILED":
      return { label: "Failed", className: "bg-red-100 text-red-700" };
    case "CANCELLED":
      return { label: "Cancelled", className: "bg-gray-100 text-gray-500" };
    default:
      return scheduledInFuture
        ? { label: "Scheduled", className: "bg-orange-100 text-orange-700" }
        : { label: "Sent", className: "bg-green-100 text-green-700" };
  }
};

export const NotificationsTable = () => {
  /** Hooks */
  const { sentNotifications, deleteNotification, retryNotification } =
    useNotifications();
  /** State */
  const [isOpen, setIsOpen] = useState(false);

  const toggleModal = () => setIsOpen(!isOpen);

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
        <thead className="font-medium bg-gray-50">
          <tr>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Title
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              body
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Created at
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Scheduled at
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Status
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {sentNotifications?.map((item) => {
            return (
              <tr
                key={item._id}
                className="bg-white hover:bg-gray-100 cursor-pointer transition ease-linear border-b"
              >
                <td className="px-2 sm:px-6 py-1 sm:py-4 break-all">
                  {item?.notification?.title}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4">
                  {item?.notification?.body}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                  {dayjs(item?.createdAt).fromNow()}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                  {dayjs(item?.scheduleDate).format("DD/MM/YYYY HH:mm")}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                  {(() => {
                    const status = describeStatus(item);
                    const total = item?.progress?.usersTotal ?? 0;
                    const done = item?.progress?.usersDone ?? 0;
                    return (
                      <div className="min-w-[140px]">
                        <span
                          className={`inline-flex items-center rounded-full text-xs font-medium px-2 py-0.5 ${status.className}`}
                          title={item?.error ?? undefined}
                        >
                          {status.label}
                        </span>
                        {item?.status === "RUNNING" ? (
                          <div className="mt-1 h-1.5 w-full rounded bg-gray-200 overflow-hidden">
                            <div
                              className="h-full bg-blue-500 transition-all"
                              style={{ width: `${status.percent ?? 0}%` }}
                            />
                          </div>
                        ) : null}
                        {item?.status === "DONE" && total > 0 ? (
                          <p className="text-[10px] text-gray-500 mt-1">
                            {done.toLocaleString()} recipients
                          </p>
                        ) : null}
                        {/* A job can finish while the push provider refused
                            some devices. Without this the row looks fully
                            delivered and "it never arrived" is unexplainable. */}
                        {(item?.progress?.tokensFailed ?? 0) > 0 ? (
                          <p className="text-[10px] text-orange-600 mt-1">
                            {item.progress?.tokensFailed} device
                            {item.progress?.tokensFailed === 1 ? "" : "s"} rejected
                            by the push service
                          </p>
                        ) : null}
                        {item?.status === "FAILED" && item?.error ? (
                          <p className="text-[10px] text-red-600 mt-1 max-w-[220px] truncate">
                            {item.error}
                          </p>
                        ) : null}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end gap-3 cursor-pointer">
                  {item?.status === "FAILED" || item?.status === "CANCELLED" ? (
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      onClick={() => retryNotification(item._id as string)}
                    >
                      Retry
                    </button>
                  ) : null}
                  <TrashIcon
                    onClick={() => {
                      const confirmDelete = window.confirm(
                        "Are you sure you want to delete this Notification?"
                      );
                      if (confirmDelete) {
                        deleteNotification(item._id as string);
                      }
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <DeleteModal isOpen={isOpen} closeModal={toggleModal} />
    </div>
  );
};
